// FrontlineTimeline — the front-line phone follow-up on a verification card
// (RRA v2.1 / W3, spec §3.W3.12).
//
//   Front line · asked {automatically | by <name>} · {date}
//   Next call {date}
//   {pending address / new number, each with its button}
//   {paused / stopped line, with Resume calls}
//   calls, NEWEST FIRST:  {callerName} · {when} · {outcome}{: reason}{ · call back <when>}
//     the call note on its own muted line with "Remove note" (G4: console only),
//     a removed note reads "Note removed by {name} · {date}"
//
// Names, outcomes and dates only — no id is rendered (the callId is the remove-note
// handler's argument and nothing else). Every wording comes from lib/rraVerificationUi
// (rraCopy for the shared words; DRAFT console lines are marked there).

import type { RraVerificationView } from '@/lib/rraStaffClient';
import {
  callRowText,
  frontlineHeader,
  frontlineStatusLine,
  nextCallLine,
  noteRemovedLine,
  pendingPhoneLine,
  pendingWritingLine,
} from '@/lib/rraVerificationUi';

const MUTED = { color: 'var(--fg-muted)', fontSize: 'var(--text-xs)' } as const;

export function FrontlineTimeline({
  verification,
  requesterName,
  signerName,
  busy,
  onResume,
  onConfirmWriting,
  onConfirmPhone,
  onRemoveNote,
  now = Date.now(),
}: {
  verification: RraVerificationView;
  requesterName: string;
  signerName: string;
  /** The page's in-flight action key (any non-null disables every button). */
  busy: string | null;
  onResume: () => void;
  onConfirmWriting: (email: string) => void;
  onConfirmPhone: (phone: string) => void;
  onRemoveNote: (callId: string) => void;
  now?: number;
}) {
  const f = verification.frontline;
  if (!f) return null;
  const open = !verification.response && verification.status !== 'cancelled';
  const calls = [...f.calls].reverse(); // newest first
  const next = open ? nextCallLine(f) : null;
  const status = open ? frontlineStatusLine(f, requesterName, now) : null;
  const writing = open && f.pendingWriting ? pendingWritingLine(f.pendingWriting.email) : null;
  const phone = open && f.pendingPhone ? pendingPhoneLine(f.pendingPhone.phone) : null;
  const names = { requesterName, signerName };

  return (
    <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
      <div style={{ fontWeight: 'var(--weight-medium)' }}>{frontlineHeader(f)}</div>
      {next && <div style={{ ...MUTED, marginTop: 'var(--space-1)' }}>{next}</div>}

      {writing && f.pendingWriting && (
        <div className="cluster" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)', alignItems: 'center' }}>
          <span>{writing.text}</span>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="xs"
            disabled={busy !== null}
            onClick={() => onConfirmWriting(f.pendingWriting!.email)}
          >
            {writing.button}
          </button>
        </div>
      )}
      {phone && f.pendingPhone && (
        <div className="cluster" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)', alignItems: 'center' }}>
          <span>{phone.text}</span>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="xs"
            disabled={busy !== null}
            onClick={() => onConfirmPhone(f.pendingPhone!.phone)}
          >
            {phone.button}
          </button>
        </div>
      )}
      {status && (
        <div className="cluster" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)', alignItems: 'center' }}>
          <span>{status.text}</span>
          <button
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="xs"
            disabled={busy !== null}
            onClick={onResume}
          >
            Resume calls
          </button>
        </div>
      )}

      {calls.length > 0 && (
        <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 'var(--space-4)' }}>
          {calls.map((c) => (
            <li key={c.callId}>
              {callRowText(c, names)}
              {c.note && (
                <div className="cluster" style={{ ...MUTED, gap: 'var(--space-2)', alignItems: 'center' }}>
                  <span>{c.note}</span>
                  <button
                    type="button"
                    className="btn"
                    data-variant="ghost"
                    data-size="xs"
                    disabled={busy !== null}
                    onClick={() => onRemoveNote(c.callId)}
                  >
                    Remove note
                  </button>
                </div>
              )}
              {!c.note && c.noteRemoved && <div style={MUTED}>{noteRemovedLine(c.noteRemoved)}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
